import { Link } from 'react-router-dom';
import ArticleLayout from '@/components/shared/ArticleLayout';

const CostOfLivingVsSalary = () => (
  <ArticleLayout
    slug="cost-of-living-vs-salary"
    title="Cost of Living vs. Salary: Why a $90k Job Can Beat a $120k Job"
    description="A bigger salary in an expensive city can leave you with less real purchasing power than a smaller one elsewhere. Here is how Regional Price Parities work, how to do the adjustment yourself, and where the simple math breaks down."
    category="Salary Data"
    datePublished="2026-02-03"
    dateModified="2026-06-01"
    readTime="10 min read"
    faqItems={[
      {
        question: 'What is a Regional Price Parity (RPP)?',
        answer:
          'Regional Price Parities are published by the U.S. Bureau of Economic Analysis and measure how prices for goods, services, and housing in a state or metro area compare with the national average, which is set to 100. An RPP of 112 means prices run about 12% above the national average; an RPP of 90 means about 10% below.',
      },
      {
        question: 'How do I adjust a salary for cost of living?',
        answer:
          'Divide the salary by the area RPP and multiply by 100. A $120,000 salary in an area with an RPP of 115 is worth about $104,300 in national purchasing power, while $90,000 in an area with an RPP of 88 is worth about $102,300 — nearly the same real value despite a $30,000 nominal gap.',
      },
      {
        question: 'Does cost-of-living adjustment account for taxes?',
        answer:
          'No. RPPs measure prices, not taxes. State income tax differences can move take-home pay by several percentage points and should be checked separately. A salary and tax calculator covers that side of the comparison.',
      },
      {
        question: 'Should remote workers use the RPP of their employer or their home?',
        answer:
          'Your purchasing power depends on where you spend, so use the RPP of where you live. This is exactly why remote work can be financially powerful: earning a wage set in a high-RPP labor market while living in a low-RPP area effectively raises your real income.',
      },
    ]}
    relatedLinks={[
      {
        title: 'Real Wages Explained',
        description: 'The live RPP table for every state, and how the toggle on our salary pages works.',
        href: '/real-wages',
      },
      {
        title: 'How to Read Salary Percentiles',
        description: 'The other half of judging an offer: where a number sits in the wage distribution.',
        href: '/insights/how-to-read-salary-percentiles',
      },
      {
        title: 'Best States by Occupation',
        description: 'Rank all 50 states for your occupation, with cost-of-living adjustment built in.',
        href: '/best-states',
      },
      {
        title: 'Tax Calculator',
        description: 'Estimate federal and state taxes — the piece RPP adjustment leaves out.',
        href: '/calculators/tax',
      },
      {
        title: 'Our Methodology',
        description: 'How Wage Atlas combines BLS wage data with BEA price data, and the limitations involved.',
        href: '/methodology',
      },
    ]}
  >
    <p>
      Here is a comparison almost everyone gets wrong at least once. You are choosing between two
      offers: $120,000 in a major coastal metro and $90,000 in a mid-sized inland city. The first
      number is a third larger, and a third is a lot. Most people take the bigger number, move, and
      then discover over the following year that rent, childcare, insurance, groceries, and a
      parking spot have quietly eaten the entire difference — and sometimes more.
    </p>
    <p>
      The problem is that a salary is denominated in dollars, but you live on what those dollars{' '}
      <em>buy</em>. Comparing salaries across locations without adjusting for local prices is like
      comparing temperatures in Fahrenheit and Celsius without converting. The conversion exists,
      it is published by a federal agency, and it takes one line of arithmetic.
    </p>

    <h2>Regional Price Parities: the official conversion rate</h2>
    <p>
      The U.S. Bureau of Economic Analysis (BEA) publishes <strong>Regional Price Parities</strong>{' '}
      (RPPs) every year for all 50 states, the District of Columbia, and every metropolitan area.
      An RPP expresses how local prices compare with the national average, which is indexed to 100:
    </p>
    <ul>
      <li>An RPP of <strong>115</strong> means goods, services, and housing cost about 15% more than the national average.</li>
      <li>An RPP of <strong>100</strong> means prices match the national average.</li>
      <li>An RPP of <strong>88</strong> means prices run about 12% below the national average.</li>
    </ul>
    <p>
      RPPs are built from the same price data that feeds the Consumer Price Index, combined with
      housing cost data from the American Community Survey. Housing is the dominant driver:
      groceries might cost 10% more in an expensive metro, but rent can cost 150% more, and rents
      carry heavy weight in the index. That is why the spread between the cheapest and most
      expensive states is so wide — roughly 87 to 113 at the state level, and wider still between
      individual metros.
    </p>

    <h2>The one-line adjustment</h2>
    <p>To convert a nominal salary into national purchasing power, divide by the RPP and multiply by 100:</p>
    <p>
      <strong>Real (adjusted) salary = Nominal salary ÷ RPP × 100</strong>
    </p>
    <p>Run the two offers from the opening through it. Suppose the coastal metro has an RPP of 115 and the inland city 88:</p>
    <ul>
      <li>$120,000 ÷ 115 × 100 = <strong>$104,300</strong> of national purchasing power</li>
      <li>$90,000 ÷ 88 × 100 = <strong>$102,300</strong> of national purchasing power</li>
    </ul>
    <p>
      The $30,000 gap collapses to about $2,000 — less than 2%. And this is before state taxes,
      which often tilt the comparison further toward the lower-cost location. The "obviously
      better" offer was, in real terms, a coin flip.
    </p>
    <p>
      Every salary page on Wage Atlas has an RPP toggle that performs this adjustment for you, using
      the BEA parity for the state or metro you are viewing. Flip it on when you compare locations;
      flip it off when you want the raw figures employers actually quote. For the current RPP of
      every state and a fuller walkthrough of the adjustment, see{' '}
      <Link to="/real-wages">Real Wages Explained</Link>.
    </p>

    <h2>When the big-city premium still wins</h2>
    <p>
      None of this means high-cost areas are a mistake. Three factors routinely justify the
      premium, and an honest comparison should weigh all of them:
    </p>
    <p>
      <strong>1. Career compounding.</strong> Expensive metros are expensive partly because they
      concentrate the highest-paying employers and the densest professional networks in certain
      fields. Five years in a major tech or finance hub can move you several percentiles up the
      national wage distribution — a gain you carry with you if you later relocate somewhere
      cheaper. The RPP adjustment measures this year's purchasing power, not the trajectory.
    </p>
    <p>
      <strong>2. Savings are spent later, possibly elsewhere.</strong> RPP adjustment assumes you
      spend your income where you earn it. Money you save and invest is different: a dollar saved
      in San Jose is worth exactly as much in retirement as a dollar saved in San Antonio. For
      aggressive savers, the relevant comparison is closer to <em>nominal salary minus local cost
      of a fixed lifestyle</em>, which favors high salaries more than the simple ratio does.
    </p>
    <p>
      <strong>3. Occupation-specific geography.</strong> Some occupations barely exist outside
      certain markets. The adjusted wage for an aerospace engineer in a state with three aerospace
      jobs is not actionable information. Check employment levels — shown on every Wage Atlas
      salary page — alongside the wage itself.
    </p>

    <h2>Where the simple math breaks down</h2>
    <p>Treat the RPP adjustment as a first-order correction, not the final word. Its known blind spots:</p>
    <ul>
      <li>
        <strong>Taxes.</strong> RPPs measure prices only. Two states with identical RPPs can differ
        by 5+ points of take-home pay once income tax enters. Use our{' '}
        <Link to="/calculators/tax">tax calculator</Link> for that half of the picture.
      </li>
      <li>
        <strong>Your personal basket.</strong> The index weights reflect average spending. If you
        own your home outright, housing-driven RPP differences overstate your exposure. If you rent
        in the urban core with two kids in daycare, they may understate it.
      </li>
      <li>
        <strong>Within-metro variation.</strong> A metro-level RPP averages downtown and distant
        suburbs. Your actual rent depends on a much smaller geography than the statistic covers.
      </li>
      <li>
        <strong>Benefits.</strong> Employer health coverage, retirement matching, and equity are
        invisible to both the wage data and the price data, and they can be worth tens of thousands
        of dollars a year.
      </li>
    </ul>

    <h2>The remote-work arbitrage</h2>
    <p>
      The adjustment formula also explains the most important personal-finance development of the
      past decade: remote work lets some workers separate the RPP at which their wage is set from
      the RPP at which they spend it. A salary benchmarked to a 110-RPP labor market, spent in an
      88-RPP town, is a 25% real raise that shows up on no pay stub. Employers know this too —
      which is why location-adjusted pay bands exist — but plenty of remote roles still price
      nationally. When you evaluate one, run both numbers: the salary against the national
      distribution for your occupation (see{' '}
      <Link to="/insights/how-to-read-salary-percentiles">our percentile guide</Link>), and the
      salary against your local RPP.
    </p>

    <h2>A five-minute comparison checklist</h2>
    <ol>
      <li>Look up the occupation's wage distribution in each location with the <Link to="/search">salary search</Link>.</li>
      <li>Toggle on the RPP adjustment to put both offers in national purchasing power.</li>
      <li>Estimate take-home pay in each state with the <Link to="/calculators/tax">tax calculator</Link>.</li>
      <li>Check employment levels — make sure the cheaper market actually has a labor market for you.</li>
      <li>Weigh trajectory: which market moves you up the distribution faster over five years?</li>
    </ol>
    <p>
      Salary is the most visible number in a job offer and the least complete. Ten minutes of
      adjustment converts it from a headline into a decision.
    </p>
  </ArticleLayout>
);

export default CostOfLivingVsSalary;
